import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../../components/AppIcon';
import { API_ENDPOINTS } from '../../../utils/config';
import Button from '../../../components/ui/Button';

const UrgentTasksPanel = () => {
  const navigate = useNavigate();
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUrgentTasks();
  }, []);

  const fetchUrgentTasks = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Fetch properties and clients
      const [propertiesRes, clientsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROPERTY_LIST, { headers }).catch(() => ({ data: [] })),
        axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers }).catch(() => ({ data: [] }))
      ]);

      const propertiesData = Array.isArray(propertiesRes.data) 
        ? propertiesRes.data 
        : (propertiesRes.data?.results || propertiesRes.data?.data || propertiesRes.data?.properties || []);

      const clientsData = Array.isArray(clientsRes.data) 
        ? clientsRes.data 
        : (clientsRes.data?.results || clientsRes.data?.data || clientsRes.data?.clients || []);

      // Normalize status for filtering (handles various API formats)
      const needsAttention = (p) => {
        const s = (p?.status || '').toString().toLowerCase().replace(/\s+/g, '_');
        return !s || s === 'pending' || s === 'in_progress' || s === 'pending_review' || s.includes('pending') || s.includes('in_progress');
      };

      // Get properties that need attention (pending or in-progress), sorted by newest first
      const pendingProperties = propertiesData
        .filter(needsAttention)
        .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
        .slice(0, 4);

      // Map to tasks
      const mappedTasks = pendingProperties.map((property, index) => {
        const client = clientsData.find(c => c.id === property.client_id || c.client_id === property.client_id);
        const createdDate = new Date(property.created_at || property.createdAt || Date.now());
        const daysSinceCreation = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const address = property.property_address || property.address || 'Unknown address';
        const propertyType = property.property_type || property.propertyType || 'Property';
        
        // Determine priority based on days since creation
        let priority = 'low';
        if (daysSinceCreation > 7) priority = 'high';
        else if (daysSinceCreation > 3) priority = 'medium';

        return {
          id: property.id || index + 1,
          type: "property_review",
          title: "Property Review Required",
          description: `${propertyType} at ${address} needs review`,
          dueDate: new Date(createdDate.getTime() + Math.max(0, 7 - daysSinceCreation) * 24 * 60 * 60 * 1000),
          priority: priority,
          client: client?.name || client?.client_name || property?.customer_name || property?.bank_name || 'Unknown Client',
          property: address
        };
      });

      setUrgentTasks(mappedTasks);
    } catch (error) {
      // Failed to fetch urgent tasks handled silently
      setUrgentTasks([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'deadline': return 'Clock';
      case 'approval': return 'CheckCircle';
      case 'appointment': return 'Calendar';
      case 'follow_up': return 'Phone';
      default: return 'AlertCircle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-muted/50';
      default: return 'bg-muted/50';
    }
  };

  const formatDueDate = (date) => {
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `${diffDays} days`;
    return date?.toLocaleDateString();
  };

  const handleTaskAction = (taskId, action) => {
    if (action === 'complete' || action === 'reschedule') {
      navigate('/property-management');
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          <h3 className="text-lg font-semibold text-foreground">Urgent Tasks</h3>
        </div>
        <span className="text-sm text-muted-foreground">
          {urgentTasks?.filter(task => task?.priority === 'high')?.length} high priority
        </span>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : urgentTasks?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="CheckCircle" size={48} color="var(--color-success)" />
          <p className="text-muted-foreground mt-4">No urgent tasks</p>
          <p className="text-sm text-muted-foreground mt-2">All caught up!</p>
        </div>
      ) : (
      <div className="space-y-4">
        {urgentTasks?.map((task) => (
          <div 
            key={task?.id} 
            className={`p-4 rounded-lg border transition-smooth hover:shadow-elevation-1 ${getPriorityBg(task?.priority)}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Icon 
                    name={getTaskIcon(task?.type)} 
                    size={18} 
                    color="var(--color-primary)" 
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground mb-1">
                    {task?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {task?.description}
                  </p>
                </div>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getPriorityColor(task?.priority)} ${getPriorityBg(task?.priority)}`}>
                {task?.priority}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-1">
                  <Icon name="User" size={12} />
                  <span className="truncate max-w-24">{task?.client}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="MapPin" size={12} />
                  <span className="truncate max-w-32">{task?.property}</span>
                </span>
              </div>
              <span className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>{formatDueDate(task?.dueDate)}</span>
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => handleTaskAction(task?.id, 'complete')}
                iconName="Check"
                iconPosition="left"
                className="flex-1"
              >
                Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTaskAction(task?.id, 'reschedule')}
                iconName="Calendar"
                iconPosition="left"
              >
                Reschedule
              </Button>
            </div>
          </div>
        ))}
        </div>
      )}
      {urgentTasks?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={() => navigate('/property-management')}
            iconName="ArrowRight"
            iconPosition="right"
            className="w-full"
          >
            View All Tasks
          </Button>
        </div>
      )}
    </div>
  );
};

export default UrgentTasksPanel;